import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { alpha, Collapse, styled } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { typography } from '@mui/system';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowDownIcon, ArrowUpIcon } from 'assets/svg';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import type { Call } from 'features/call/call-base/interfaces';
import { toggleAutoComplete } from 'features/call/call-base/store';
import { updateCallRecord } from 'features/call/call-base/store/callSlice';
import {
  callSelector,
  selectIsAutoCompleteEnabled,
} from 'features/call/call-base/store/selectors';
import {
  CallDetailsAccordianHeaderMenu,
  CallDetailsAccordianLanguageMenu,
} from 'features/call/call-details/components';
import { Separator } from 'features/call/call-details/styles';
import { EndSessionButton } from 'features/call/call-finish/components';
import { selectIsFullyDisconnected } from 'features/caller-session/store';
import { isAcceptingUserSelector } from 'features/teaming/teaming-base/store';
import theme from 'features/theme/theme';

interface CallDetailsAccordianMenuProps {
  children: ReactNode;
}

export const CallDetailsAccordianMenu = ({
  children,
}: CallDetailsAccordianMenuProps) => {
  const dispatch = useAppDispatch();
  const call = useAppSelector(callSelector);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [languageMenuAnchorEl, setLanguageMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const { t } = useTranslation('translation');

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearMenuTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const isFullyDisconnected = useAppSelector(selectIsFullyDisconnected);
  const autoCompleteEnabled = useAppSelector(selectIsAutoCompleteEnabled);
  const isAcceptingUser = useAppSelector(isAcceptingUserSelector);
  const MENU_CLOSE_DELAY = 500;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    clearMenuTimeout();
    setMenuAnchorEl(null);
    setLanguageMenuAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchorEl(event.currentTarget);
  };

  const handleAccordionToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const updateLanguage = (lang: string) => {
    const partialCall: Partial<Call> = {
      LanguageCode: lang,
    };
    dispatch(updateCallRecord(partialCall));
    clearMenuTimeout();
    setTimeout(() => {
      handleMenuClose();
    }, MENU_CLOSE_DELAY);
  };

  const handleAutoCompleteToggle = () => {
    dispatch(toggleAutoComplete());
    clearMenuTimeout();
    setTimeout(() => {
      handleMenuClose();
    }, MENU_CLOSE_DELAY);
  };

  useEffect(() => {
    return () => {
      clearMenuTimeout();
    };
  }, []);

  return (
    <RootContainer>
      <ContainerInnerWrapper $isExpanded={isExpanded}>
        <Header>
          <TitleWithIcon>
            <Title>{t('callDetails.title')}</Title>
          </TitleWithIcon>
          <ButtonContainer>
            {!isAcceptingUser && (
              <StyledIconButton
                onClick={handleMenuOpen}
                data-testid='call-details-menu-bar-button'
              >
                <IconWrapper>
                  <MoreHorizIcon />
                </IconWrapper>
              </StyledIconButton>
            )}
            <Tooltip
              title={isExpanded ? t('vrsChat.collapse') : t('vrsChat.expand')}
              placement='top'
            >
              <span>
                <StyledIconButton
                  onClick={handleAccordionToggle}
                  data-testid='accordion-toggle-button'
                >
                  <IconWrapper>
                    {isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
                  </IconWrapper>
                </StyledIconButton>
              </span>
            </Tooltip>
          </ButtonContainer>
        </Header>

        <Collapse
          in={isExpanded}
          data-expanded={isExpanded}
          data-testid='call-details-collapse'
        >
          <Separator />
          {children}
        </Collapse>
      </ContainerInnerWrapper>
      {isFullyDisconnected && !autoCompleteEnabled && <EndSessionButton />}

      <CallDetailsAccordianHeaderMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onLanguageMenuOpen={handleLanguageMenuOpen}
        onAutoCompleteToggle={handleAutoCompleteToggle}
      />

      <CallDetailsAccordianLanguageMenu
        anchorEl={languageMenuAnchorEl}
        open={Boolean(languageMenuAnchorEl)}
        onClose={handleMenuClose}
        selectedLanguage={call.languageCode}
        onLanguageSelect={updateLanguage}
      />
    </RootContainer>
  );
};

const RootContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${theme.palette.grey[900]};
`;

const ContainerInnerWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isExpanded',
})<{ $isExpanded?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${({ $isExpanded }) =>
    $isExpanded ? theme.spacing(14, 0, 0) : theme.spacing(14, 0, 12)};
`;

const StyledIconButton = styled('button')`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing(4)};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: ${alpha(theme.palette.common.white, 0.7)};

  &:hover {
    background: ${alpha(theme.palette.common.white, 0.1)};
    color: ${theme.palette.common.white};
  }

  &:active {
    background: ${alpha(theme.palette.common.white, 0.2)};
  }
`;

const ButtonContainer = styled('div')`
  display: flex;
  gap: ${theme.spacing(0)};
  align-items: center;
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleWithIcon = styled('div')`
  align-items: center;
  display: flex;
  padding: ${theme.spacing(0, 12, 0)};
`;

const IconWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled('div')`
  ${typography(theme.typography.body1)};
  color: ${theme.palette.common.white};
`;
